import type { SelectedProfessions } from "@brookbush/types";

import { useAuth, useUser } from "@clerk/nextjs";
import { useRouter } from "next/compat/router";
import posthog from "posthog-js";
import toast from "react-hot-toast";
import { mutate as globalMutate } from "swr";
import useSWRImmutable from "swr/immutable";

import { useSelectedCertification } from "~/hooks";
import { getApiErrorMessage } from "~/lib/getApiErrorMessage";
import { revalidateProfessionCaches } from "~/lib/profession/revalidateProfessionCaches";
import fetcher from "~/utils/fetcher.client";

const useSelectedProfessions = () => {
	const router = useRouter();
	const { user } = useUser();
	const { userId } = useAuth();

	const {
		data: selectedProfessions,
		mutate,
		...rest
	} = useSWRImmutable<SelectedProfessions>(userId && `/professions/selected`);

	const { selectedCertification, handleSelectCertification } =
		useSelectedCertification();

	const handleChangeProfession = async (data: SelectedProfessions) => {
		if (!data.primaryProfession?._id) {
			toast.error("Select a primary profession before saving.");
			throw new Error("Missing primary profession");
		}

		await toast.promise(
			(async () => {
				const result = await mutate(
					fetcher(`/professions/selected`, {
						method: "PUT",
						data,
						headers: {
							"x-posthog-session-id": posthog.get_session_id(),
						},
					}),
				);
				await revalidateProfessionCaches(globalMutate);
				return result;
			})(),
			{
				loading: "Saving...",
				success: "Changes saved",
				error: (error) => getApiErrorMessage(error, "Error saving."),
			},
		);

		if (selectedCertification?.slug === "imt") {
			await handleSelectCertification({
				slug: "courses",
				_id: "courses",
				title: "All Courses",
				focus: "For Continuing Education",
			});

			router?.replace("/");
		}

		if (data.cataMemberNumber) {
			await user?.update({
				unsafeMetadata: {
					...user?.unsafeMetadata,
					cataMemberNumber: data.cataMemberNumber,
				},
			});
		}

		globalMutate(`/certifications`);
		globalMutate("/onboarding/checklist");
	};

	return {
		selectedProfessions,
		handleChangeProfession,
		isLoadingSelectedProfessions: rest.isLoading,
		...rest,
	};
};

export default useSelectedProfessions;
